import React from 'react';
import CustomTooltip from './CustomTooltip';
import { FaStar } from 'react-icons/fa';

/**
 * A reusable skill pill component that displays a skill with optional seniority level and years of experience
 * 
 * @param {Object} props - Component props
 * @param {string} props.name - The name of the skill
 * @param {string} props.seniority - Optional seniority level (e.g., "senior", "middle", "junior")
 * @param {string|number} props.yearsExperience - Optional years of experience
 * @param {string} props.className - Optional additional CSS classes
 * @param {string} props.title - Optional tooltip text to display on hover
 * @param {boolean} props.isTop5 - Whether this skill is marked as a top5 skill
 * @param {string} props.gradeSource - Optional source of the skill grade
 * @param {number} props.numberOfMentions - Optional number of mentions of the skill
 * @param {number} props.inferredYearsExperience - Optional inferred years of experience with the skill
 * @param {Array} props.context - Optional array of context strings to display as bullet points
 * @returns {JSX.Element} - The rendered component
 */
const SkillPill = ({ name, seniority, yearsExperience, className = '', title, isTop5, gradeSource, numberOfMentions, inferredYearsExperience, context }) => {
  // Determine the background color based on the seniority level and top5 status
  const getBgColorClass = (seniority) => {
    // If it's a top5 skill, use gold color regardless of seniority
    if (isTop5) return 'bg-yellow-300 text-yellow-900 border-yellow-400';
    
    if (!seniority) return 'bg-gray-100 text-gray-800 border-gray-200';
    
    const level = seniority.toLowerCase();
    if (level === 'senior') return 'bg-green-100 text-green-800 border-green-200';
    if (level === 'middle') return 'bg-blue-100 text-blue-800 border-blue-200';
    if (level === 'junior') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };
  
  // Get the lighter background color for the details section
  const getDetailsBgColorClass = (seniority) => {
    // If it's a top5 skill, use lighter gold color regardless of seniority
    if (isTop5) return 'bg-yellow-200 text-yellow-900';
    
    if (!seniority) return 'bg-gray-50 text-gray-800';
    
    const level = seniority.toLowerCase();
    if (level === 'senior') return 'bg-green-50 text-green-800';
    if (level === 'middle') return 'bg-blue-50 text-blue-800';
    if (level === 'junior') return 'bg-yellow-50 text-yellow-800';
    
    return 'bg-gray-50 text-gray-800';
  };
  
  const bgColorClass = getBgColorClass(seniority);
  const detailsBgColorClass = getDetailsBgColorClass(seniority);
  
  const content = (
    <div className={`inline-flex items-stretch rounded-full overflow-hidden border border-gray-200 w-fit ${className} ${title ? 'cursor-help' : ''}`}>
      <span className={`py-2 px-4 font-bold flex items-center ${bgColorClass}`}>
        {isTop5 && <FaStar className="inline mr-1 text-yellow-600" />}
        {name}
      </span>
      <div className={`px-3 flex items-center ${detailsBgColorClass}`}>
        {seniority && (
          <span className="text-sm font-medium">{seniority}</span>
        )}
        {seniority && yearsExperience && (
          <span className="mx-2 text-gray-400">|</span>
        )}
        {yearsExperience && (
          <span className="text-sm">{yearsExperience} years</span>
        )}
      </div>
    </div>
  );
  
  // Create tooltip content with additional information if available
  const hasAdditionalInfo = gradeSource || numberOfMentions || inferredYearsExperience || (context && context.length > 0);
  
  const tooltipContent = hasAdditionalInfo
    ? <div>
        {title}
        {context && context.length > 0 && (
          <div className="mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm">
              {context.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        )}
        {title && (gradeSource || numberOfMentions || inferredYearsExperience) && <hr className="my-2 border-gray-600" />}
        {gradeSource && <div className="text-sm mt-1">Grade source: {gradeSource}</div>}
        {numberOfMentions && <div className="text-sm mt-1">Number of mentions: {numberOfMentions}</div>}
        {inferredYearsExperience && <div className="text-sm mt-1">Inferred years of experience: {inferredYearsExperience}</div>}
      </div>
    : title;

  return tooltipContent ? (
    <CustomTooltip content={tooltipContent} className="text-lg">
      {content}
    </CustomTooltip>
  ) : content;
};

export default SkillPill;
